import axios from "axios";
import { API_URL } from "../MasterData/index";

/**
 * Get authorization token from localStorage
 * @returns {string|null} Authorization token
 */
const getAuthToken = () => {
  return localStorage.getItem("token") || localStorage.getItem("authToken");
};

/**
 * Handle 401 Unauthorized responses
 * Shows session expired modal and logs out user
 */
const handle401Error = () => {
  // Show session expired modal
};

/**
 * Common fetch wrapper with token and 401 handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();

  // Add authorization header if token exists
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized
  if (response.status === 401) {
    handle401Error();
    throw new Error("Session expired");
  }

  return response;
};

/**
 * Axios instance with interceptors for token and 401 handling
 */
const axiosInstance = axios.create();

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      handle401Error();
    }
    return Promise.reject(error);
  }
);

// User Login API (no token required for login)
export const userLogin = async (requestPayload) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-auth/OAuth2Client/1`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.log("Login error:", error);
    return error;
  }
};

// Google OAuth Login API
export const googleLogin = async (googleToken, userEmail, userName) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-auth/google-login/4`,
      {
        googleToken: googleToken,
        email: userEmail,
        name: userName,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.log("Google login error:", error);
    return error;
  }
};

// Google SSO Login API (with credential only)
export const googleSsoLogin = async (googleCredential) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-auth/OAuth2Client/login`,
      {
        googleToken: googleCredential,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.log("Google SSO login error:", error);
    return error;
  }
};

// Change Password API
export const changePassword = async (userData) => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/user-auth/change-password/2`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      }
    );

    return await response.json();
  } catch (error) {
    console.error("Error changing password:", error);
    return { status: false, message: error.uiMsg };
  }
};

// Add this function to your API utilities

export const verifyUserId = async (userId) => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/user-auth/verify-userId/3`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error verifying userId:", error);
    throw error;
  }
};

/**
 * Fetch holidays and weekend configuration from API
 * @param {string} countryCode - Country code (e.g., 'IN' for India)
 * @returns {Promise<Object>} Holiday and weekend configuration data
 */
export const fetchHolidaysAPI = async (countryCode = "IN") => {
  try {
    console.log(`Fetching holidays for country code: ${countryCode}...`);

    const response = await fetchWithAuth(
      `${API_URL}/manage-office/holiday?countryCode=${countryCode}`
    );

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log("Holiday API response:", result);

    if (!result.status || !result.data) {
      throw new Error("Invalid response format or status is false");
    }

    // Separate holidays from Saturday/Sunday configuration
    const holidays = [];
    let saturdayWorking = false;
    let sundayWorking = false;

    result.data.forEach((item) => {
      if (item.holidayStatus === "true" || item.holidayStatus === true) {
        // Check if it's Saturday or Sunday configuration
        if (item.holidayName.toLowerCase() === "saturday") {
          saturdayWorking = false; // If Saturday is in the list with holidayStatus=true, it's a non-working day
        } else if (item.holidayName.toLowerCase() === "sunday") {
          sundayWorking = false; // If Sunday is in the list with holidayStatus=true, it's a non-working day
        } else if (item.holidayStartDate) {
          // It's a public holiday with a date
          holidays.push({
            id: item.id,
            date: item.holidayStartDate,
            name: item.holidayName,
            countryCode: item.countryCode,
          });
        }
      } else {
        // If holidayStatus is false, it means it's a working day
        if (item.holidayName.toLowerCase() === "saturday") {
          saturdayWorking = true;
        } else if (item.holidayName.toLowerCase() === "sunday") {
          sundayWorking = true;
        }
      }
    });

    return {
      holidays,
      saturdayWorking,
      sundayWorking,
      countryCode,
    };
  } catch (error) {
    console.error("Error fetching holidays:", error);
    throw error;
  }
};
